import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getChannel, listVideos, toggleSubscribe, mySubscriptions, resolveFileUrl, getChannelMusic, channelSpotify } from '../lib/api';
import VideoCard from '../components/VideoCard';
import ChannelSponsorBlock from '../components/ChannelSponsorBlock';
import { CheckCircle2, Bell, Share2, Loader2, Music, Play, Pause, ExternalLink } from 'lucide-react';
import { formatCount } from '../mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';

export default function Channel() {
  const { id } = useParams();
  const { user } = useApp();
  const [channel, setChannel] = useState(null);
  const [videos, setVideos] = useState([]);
  const [music, setMusic] = useState([]);
  const [spotify, setSpotify] = useState(null);
  const [tab, setTab] = useState('videos');
  const [subbed, setSubbed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const c = await getChannel(id);
        setChannel(c);
        const [v, m] = await Promise.all([
          listVideos({ channel_id: id, limit: 200 }),
          getChannelMusic(id).catch(() => []),
        ]);
        setVideos(v);
        setMusic(m);
        if (c?.spotify_artist_id) {
          channelSpotify(id).then(setSpotify).catch(() => {});
        }
        if (user) {
          try { const subs = await mySubscriptions(); setSubbed(subs.some(s => s.id === id)); } catch {}
        }
      } catch { toast.error('Failed to load channel'); }
      finally { setLoading(false); }
    })();
  }, [id, user]);

  const liveVideos = useMemo(() => videos.filter(v => v.type === 'live'), [videos]);

  const onSub = async () => {
    if (!user) { toast.error('Please sign in'); return; }
    try { const { subscribed, subs_count } = await toggleSubscribe(id); setSubbed(subscribed); setChannel({ ...channel, subs_count }); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  if (!channel) return <div className="text-white/60 py-10">Channel not found.</div>;

  return (
    <div>
      <div className="rounded-2xl overflow-hidden relative" style={{ height: 220 }}>
        <img src={resolveFileUrl(channel.cover)} alt="cover" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0a0a0c]" />
      </div>
      <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-6 -mt-10 md:-mt-12 px-2 md:px-4 relative z-10">
        <img src={resolveFileUrl(channel.avatar)} alt={channel.name} className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-[#0a0a0c]" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-[32px] md:text-[40px] leading-none">{channel.name}</h1>
            {channel.verified && <CheckCircle2 className="w-5 h-5 text-white/60" />}
          </div>
          <div className="text-[13.5px] text-white/60 mt-1">{channel.handle} • {formatCount(channel.subs_count)} subscribers • {videos.length} videos</div>
          {spotify?.artist && (
            <a href={spotify.artist.spotify_url} target="_blank" rel="noreferrer noopener"
               className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-[12px] border transition-colors"
               style={{ color: '#1DB954', borderColor: 'rgba(29,185,84,0.45)', background: 'rgba(29,185,84,0.08)' }}
               data-testid="channel-spotify-badge">
              <Music className="w-3 h-3" /> {Intl.NumberFormat('en', { notation: 'compact' }).format(spotify.artist.followers)} on Spotify
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <p className="text-[14px] text-white/80 mt-2 max-w-[680px]">{channel.bio}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onSub} className={subbed ? 'btn-ghost' : 'btn-primary'}><Bell className="w-4 h-4" /> {subbed ? 'Subscribed' : 'Subscribe'}</button>
          <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Channel link copied'); }} className="btn-ghost"><Share2 className="w-4 h-4" /> Share</button>
        </div>
      </div>

      <ChannelSponsorBlock channel={channel} />

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="bg-white/[0.04] border border-white/10">
          <TabsTrigger value="videos">Videos</TabsTrigger>
          {music.length > 0 && <TabsTrigger value="music" data-testid="channel-tab-music"><Music className="w-3.5 h-3.5 mr-1" /> Music</TabsTrigger>}
          <TabsTrigger value="live">Live</TabsTrigger>
          <TabsTrigger value="about">About</TabsTrigger>
        </TabsList>

        <TabsContent value="videos" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {videos.map(v => <VideoCard key={v.id} video={v} />)}
            {videos.length === 0 && <div className="text-white/50">No videos yet.</div>}
          </div>
        </TabsContent>
        <TabsContent value="music" className="mt-6">
          <ChannelMusicList tracks={music} />
        </TabsContent>
        <TabsContent value="live" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {liveVideos.map(v => <VideoCard key={v.id} video={v} />)}
            {liveVideos.length === 0 && <div className="text-white/50">No live streams yet.</div>}
          </div>
        </TabsContent>
        <TabsContent value="about" className="mt-6 max-w-[720px] text-white/80 text-[14.5px] leading-relaxed">
          <p>{channel.bio || 'No bio yet.'}</p>
          <div className="hair-line my-6" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-[13px]">
            <div><div className="text-white/50">Subscribers</div><div className="font-semibold">{formatCount(channel.subs_count)}</div></div>
            <div><div className="text-white/50">Videos</div><div className="font-semibold">{videos.length}</div></div>
            <div><div className="text-white/50">Handle</div><div className="font-semibold">{channel.handle}</div></div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ChannelMusicList({ tracks }) {
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const toggle = async (t) => {
    const url = resolveFileUrl(t.file_url);
    if (!audioRef.current) return;
    if (playingId === t.id) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current.src = url;
    try { await audioRef.current.play(); setPlayingId(t.id); }
    catch { toast.error('Unable to play'); setPlayingId(null); }
  };

  if (!tracks || tracks.length === 0) return <div className="text-white/50">No music released yet.</div>;
  return (
    <div className="space-y-2" data-testid="channel-music-list">
      {tracks.map(t => (
        <div key={t.id} className="card-shell p-3 flex items-center gap-4 hover-lift" data-testid={`channel-music-${t.id}`}>
          <button onClick={() => toggle(t)} className="w-12 h-12 rounded-xl grid place-items-center shrink-0 bg-gradient-to-br from-[#ff2e63] to-[#ff7a00] hover:scale-105 transition-transform" aria-label="Play" data-testid={`channel-music-toggle-${t.id}`}>
            {playingId === t.id ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
          </button>
          <div className="relative w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-black hidden sm:block">
            <img src={resolveFileUrl(t.cover_url) || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=200'} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px] truncate">{t.title}</div>
            <div className="text-[12.5px] text-white/55 truncate">{t.artist_name || ''} {t.genre && `· ${t.genre}`} {t.bpm && `· ${t.bpm} BPM`}</div>
          </div>
          <div className="text-[12px] text-white/50 tabular-nums shrink-0">{Math.floor((t.duration_s || 0) / 60)}:{String((t.duration_s || 0) % 60).padStart(2, '0')}</div>
          {t.in_rotation && <span className="chip text-[10px] uppercase hidden md:inline-flex">in rotation</span>}
        </div>
      ))}
      <audio ref={audioRef} preload="none" onEnded={() => setPlayingId(null)} />
    </div>
  );
}

